import { useModal } from '../../../providers/ModalProvider';
import Modal from '../Modal';
import { modals } from '../../../providers/modals';
import { Text } from '../../typography';
import { Form } from 'react-bootstrap';
import { DropdownText } from '../../dropdowns/DropdownText';
import { incidentTypes } from '../../../utilities/incidents';
import UserMultiSelector from '../../UserMultiSelector';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import styles from './CreateIncidentModal.module.scss';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import Icon from '../../Icon';
import { useIncidents } from '../../../hooks/offline-hooks/incidentHook';
import { showToast } from '../../../graphql/cache/modal';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone';
import { useEffect, useMemo, useRef, useState } from 'react';
import findFileIcon, { imageExtensions } from '../../../utilities/files.js';
import ImageWrapper from '../../offline-wrapper-functions/image-wrapper.js';
import { openFileViewer } from '../../../graphql/cache/modal';
import { useMutation } from '@apollo/client';
import { loader } from 'graphql.macro';
import { getRoute, paths } from '../../../constants/strings.js';
import { useNavigate } from 'react-router-dom';
import { IncidentReportAviation } from '../../../pages/incidents/IncidentReportAviation.jsx';
import { useCurrentUser } from '../../../providers/UserProvider.jsx';
import TextWithIcon from '../../typography/TextWithIcon.jsx';
import Spacer from '../../typography/Spacer.jsx';
import { useTemplates } from '../../../providers/TemplateProvider.jsx';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook.js';
import { useMyInspections } from '../../../providers/MyInspectionsProvider.jsx';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc.js';
import { questionTypes } from '../../../utilities/questions.js';

const addImageMutation = loader(
  '../../../pages/incidents/IncidentReport.addImage.graphql',
);

export default function CreateIncidentModal() {
  const online = useOnlineStatus();
  const { updateModal, closeModal, modalState, openConfirmationModal } =
    useModal();
  const [loading, setLoading] = useState(false);
  const { templates, allTemplates } = useTemplates();
  const onHide = () => {
    openConfirmationModal({
      title: 'Close Window',
      variant: 'danger',
      buttonText: 'Close',
      description:
        'Are you sure you want to close this window? All changes will be discarded and this action cannot be undone.',
      onSubmit: () => closeModal({ modalName: modals.createIncident }),
    });
  };
  const [aviationQuestions, setAviationQuestions] = useState(null);

  const { addInspection } = useInspection();
  const { refetchMyInspections } = useMyInspections();

  const { company } = useCurrentUser();
  const customIncidentTypes = company.incidentTypes || {};

  const { workspaceId, workspace } = useWorkspace();
  const {
    show,
    modalTitle,
    type,
    subtype,
    participants,
    observation,
    description,
    dateOfIncident,
    location,
    images = [],
    navigate,
    anonymous,
    onClose = () => {},
  } = modalState.createIncidentModal;

  const isAviation = type === 'Aviation';
  const showFlightDetails =
    isAviation && !!subtype && subtype !== 'Maintenance';
  const { t } = useTranslation();
  const { addIncident } = useIncidents();
  const [additionalQuestions, setAdditionalQuestions] = useState([]);

  const update = (variables) => {
    updateModal({
      modalName: modals.createIncident,
      variables,
    });
  };
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const incident = modalState.createIncidentModal;
  const [addImage] = useMutation(addImageMutation);
  const navigation = useNavigate();

  const followUpTemplate = useMemo(() => {
    if (!!type) {
      return templates?.find((t) => {
        const allAutoTypes = t.autoIncidentType?.split('|') ?? [];
        return allAutoTypes?.includes(type) && !t.isDraft && !t.isArchived;
      });
    }
    return null;
  }, [templates, type]);

  const parsePipes = (str) =>
    (str || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);

  useEffect(() => {
    if (!type || !subtype) return null;
    const tag = `${type} - ${subtype}`;
    const template =
      allTemplates?.find(
        (tpl) =>
          tpl.templateType === 'OBSERVATION' &&
          !tpl.isDraft &&
          !tpl.isArchived &&
          parsePipes(tpl.autoIncidentType).includes(tag),
      ) || null;

    setAdditionalQuestions(
      template?.id ? template.categories?.[0]?.questions : null,
    );
  }, [allTemplates, type, subtype]);

  const onQuestionChange = (id, value) => {
    setAdditionalQuestions((prev) => {
      const next = prev.map((q) => (q.id === id ? { ...q, value } : { ...q }));
      return next;
    });
  };
  const normalizeValue = (v) => {
    if (Array.isArray(v)) return v.join('|');
    if (v === true) return t(keys.common.YES);
    if (v === false) return t(keys.common.NO);
    return v ?? '';
  };

  function onSubmit() {
    const vehicleId = isAviation
      ? aviationQuestions?.vehicle?.id || null
      : null;

    const items = additionalQuestions?.map(
      ({ id, title, value, required, inputType, additionalData }) => ({
        id,
        title,
        value: normalizeValue(value),
        required,
        inputType,
        additionalData,
      }),
    );
    addIncident({
      variables: {
        ...incident,
        participants: participants?.map((user) => user?.id) || null,
        status: 'INITIATED',
        workspaceId,
        observationId: observation?.id ?? null,
        aviationQuestions,
        vehicleId,
        anonymous,
        items,
      },
    }).then(({ data: { addIncident }, errors }) => {
      if (!errors?.length) {
        update({ id: addIncident.id });
        if (images?.length) {
          for (let image of images) {
            addImage({
              variables: {
                incidentId: addIncident.id,
                description: 'Initial Report',
                type: 'INITIAL',
                imageUrl: image.url,
                size: image.size,
                name: image.name,
                extension: image.extension,
              },
            });
          }
        }
        if (!!followUpTemplate && !observation?.id) {
          addInspection({
            templateId: followUpTemplate?.id,
            inspectionType: 'INCIDENT',
            participants: participants?.map((user) => user.id) || [],
            workspaceId,
            incidentId: addIncident.id,
            privateAssessment: true,
            anonymous,
          }).then((res) => {
            const id = res?.data?.addInspection?.id;
            const err = res?.errors;
            if (err?.length) {
              showToast({
                variant: 'warning',
                title: 'Error',
                message: `There was a problem starting the supplementary report. Error: ${err[0].message}`,
              });
            }
            if (!id) {
              return;
            }
            if (online) {
              refetchMyInspections();
            }
            const route = getRoute(workspaceId, paths.assessment, id);
            showToast({
              title: 'Supplementary Report Required',
              message: `This type of incident requires more information. Please complete ${
                followUpTemplate?.title || ' this report'
              } `,
            });
            closeModal({ modalName: modals.createIncident });
            navigation(route);
          });
        } else {
          onClose();
          closeModal({ modalName: modals.createIncident });
          if (!!navigate) {
            navigation(`/${paths.incident}/${addIncident.id}`);
          }
          showToast({
            title: t(keys.incidents.SUBMITTED),
            message: t(keys.incidents.SUBMITTED_MESSAGE),
          });
        }
      } else {
        showToast({
          variant: 'danger',
          title: 'Error',
          message: `There was a problem submitting your incident. Error: ${errors[0].message}`,
        });
      }
    });
  }
  const allRequiredQuestionsAnswered = useMemo(() => {
    if (!additionalQuestions?.length) return true;
    const requiredQuestionIds =
      additionalQuestions
        ?.filter((question) => question.required)
        .map((question) => question.id) || [];
    return requiredQuestionIds.every((id) => {
      const answer = additionalQuestions?.find((answer) => answer?.id === id);
      return answer && Boolean(answer.value);
    });
  }, [additionalQuestions]);

  return (
    <Modal
      open={show || false}
      title={t(keys.incidents.REPORT_INCIDENT) || modalTitle}
      submitDisabled={
        !type ||
        !subtype ||
        !description ||
        !dateOfIncident ||
        !allRequiredQuestionsAnswered
      }
      onClose={onHide}
      disableCloseOnSubmit={true}
      disableClickOutside={true}
      titleImage={
        <Icon style={{ fontSize: '2rem' }} color="red">
          fmd_bad
        </Icon>
      }
      onSubmit={() => {
        onSubmit();
      }}
      footer={
        !online || isAviation ? null : (
          <div className={styles.flexRow}>
            <Icon>no_accounts</Icon>
            <div>
              <Text noMargin weight="semiBold">
                {t(keys.observations.ANONYMOUS_SUBMISSION)}
              </Text>
              <Text size="xs" noMargin>
                {t(keys.utilities.ANONYMOUS_PROMISE)}
              </Text>
            </div>
            <Form.Check
              type="switch"
              onChange={(e) => update({ anonymous: e.target.checked })}
              checked={anonymous}
              data-cy="observation-anonymous-switch"
              className="form-switch form-switch-lg"
            />
          </div>
        )
      }
      className={styles.maxWidth}
    >
      <>
        <div className={styles.spacer} />
        <div className={styles.field}>
          <Text noMargin weight="semiBold">
            {`${t(keys.incidents.DATE_OF_INCIDENT)}`}
            <span className={styles.red}>*</span>
          </Text>
          <input
            className={styles.date}
            type="datetime-local"
            max={new Date().toISOString().slice(0, 16)}
            value={dateOfIncident || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              update({ dateOfIncident: newValue });
            }}
          />
        </div>
        <Text noMargin weight="semiBold">
          {`${t(keys.common.LOCATION)}`}
          <span className={styles.red}>*</span>
        </Text>
        {!!workspace?.locations ? (
          <DropdownText
            placeholder={'Location'}
            selected={location}
            items={workspace.locations.split('|')}
            onChange={(location) => update({ location })}
            onRemove={() => update({ location: null })}
          />
        ) : (
          <div>
            <Form.Control
              as="textarea"
              rows={2}
              value={location}
              onChange={(e) => update({ location: e.target.value })}
            />
            <div className={styles.spacer} />
          </div>
        )}
        <div className={styles.field}>
          <Text noMargin weight="semiBold">
            {`${t(keys.common.DESCRIPTION)}`}
            <span className={styles.red}>*</span>
          </Text>
          <Form.Control
            as="textarea"
            rows={4}
            value={description}
            onChange={(e) => update({ description: e.target.value })}
          />
        </div>
        <Text noMargin weight="semiBold">
          {`${t(keys.incidents.TYPE)}`}
          <span className={styles.red}>*</span>
        </Text>
        <DropdownText
          selected={type}
          highlight
          required={true}
          onChange={(newType) => {
            update({ type: newType, subtype: null });
          }}
          items={Object.keys(customIncidentTypes || incidentTypes)}
          placeholder={`${t(keys.incidents.TYPE)}`}
        />
        <div>
          <Text noMargin weight="semiBold">
            {`${t(keys.incidents.SUBTYPE)}`}
            <span className={styles.red}>*</span>
          </Text>
          <DropdownText
            highlight
            required={true}
            selected={subtype}
            onChange={(newSubtype) => update({ subtype: newSubtype })}
            items={customIncidentTypes[type] || incidentTypes[type]}
            placeholder={`${t(keys.incidents.SUBTYPE)}`}
            disabled={!type}
          />
        </div>
        {showFlightDetails ? (
          <div>
            <br />
            <TextWithIcon size="lg" weight="semiBold" icon="flight">
              Aviation Details
            </TextWithIcon>
            <Spacer />
            <IncidentReportAviation
              incident={incident}
              readOnly={false}
              initialReport={true}
              setAviationQuestions={setAviationQuestions}
              showHeader={false}
            />
            <hr />
          </div>
        ) : null}
        {!!additionalQuestions?.length && !!type && subtype
          ? additionalQuestions?.map((t, i) => {
              const {
                title,
                description,
                inputType,
                required,
                additionalData,
                id,
              } = t || {};
              const component = questionTypes({
                title,
                description,
                inputType,
                required,
                additionalData,
                id,
                questions: additionalQuestions,
                onChange: (val) => onQuestionChange(id, val, inputType),
              });
              return (
                <div style={{ marginBottom: '0.5rem' }} key={`${i}-${title}`}>
                  {component}
                </div>
              );
            })
          : null}
        <Spacer />
        <div className={styles.field}>
          <Text weight="semiBold" noMargin>
            {t(keys.observations.SELECT_TEAM)}
          </Text>
          <UserMultiSelector
            stateUpdateOnly
            setUsers={(participants) => update({ participants })}
            selected={participants}
            type="workspace"
            onUserAdded={(user) => {
              update({ participants: [...participants, user] });
            }}
            onUserRemoved={(user) => {
              update({
                participants: participants?.filter((u) => u.id !== user.id),
              });
            }}
          />
          <Spacer />
          <QuickFileDropzone
            allowMulti
            onSubmit={(image) => {
              const next = [...imagesRef.current, image];
              imagesRef.current = next;
              update({ images: next });
            }}
            loading={loading}
            setLoading={setLoading}
            icon={!!images.length}
            iconText={t(keys.action.ADD, { variable: t(keys.common.FILE) })}
          />
          <div className={styles.images}>
            {images.map(({ extension, url }, index) => {
              const isImage = imageExtensions.includes(extension);
              const src = isImage ? url : findFileIcon(extension);
              const file = {
                fileType: extension,
                url,
                imageUrl: url,
                downloadAllowed: true,
              };
              return (
                <div key={`image-${url}-${index}}`}>
                  <ImageWrapper
                    key={extension}
                    alt={`observation-${index}`}
                    style={{ height: '8rem', width: 'auto' }}
                    image={src}
                    className={isImage ? styles.image : styles.file}
                    onClick={() => openFileViewer(file)}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Text textAlign="center" color="secondary" weight="semiBold">
          {workspace?.incidentEmergencyMessage ?? ''}
        </Text>
      </>
    </Modal>
  );
}
